// ─────────────────────────────────────────────────────────────────────
// BANEPAD DEVICE PAIRING — persisted boundary (proof of concept)
//
// The single, REPLACEABLE seam between the pure pairing logic
// (src/engine/banepadDevice.ts) and where the registrations live. Today that
// is the local Storage abstraction (per club, via `/kv`). The
// public function names below are the API the UI talks to — keep them stable so
// the backing store can be swapped without touching callers.
//
// ── THE CREDENTIAL IS SERVER-SIDE ON THE DEPLOYED PATH (#663) ───────
//
// What used to stand here was a production marker asking to «move this boundary
// server-side, mint + store the deviceToken as a HASH, verify it on the server,
// and return the court scope from the verified token», followed by «this is
// demo-only… must not be treated as a real credential».
//
// ALL OF THAT SHIPPED, and the comment outlived it by long enough to mislead:
//
//   #185  `POST /pad/pair` mints a 256-bit token server-side
//   #184  only its SHA-256 hash is stored, in `banepad_devices.token_hash`
//   #185  `requireDeviceAuth` verifies it; `/pad/me` answers from the device row
//   #182  the court comes from that row — THE PAD NEVER NAMES ITS OWN COURT
//   #375  revoking clears the hash, so a revoked secret is not left on disk
//
// A security comment crying wolf costs the same credibility as a missing one, so
// this list was corrected rather than deleted (#664).
//
// THERE IS ONE PAIRING PATH AND IT IS THE SERVER'S (#663). The browser-minted
// fallback that used to sit in `pairBanepadDevice` is gone, so a build without an
// API refuses to pair rather than inventing a credential. Nothing in the CLIENT can
// mint a device credential; `POST /pad/pair` can, and is the only thing that does.
//
// AND A MIGRATED TABLET KEEPS NO JAVASCRIPT-READABLE CREDENTIAL (#668). The
// credential is an httpOnly, Secure, host-only `spd_session` cookie issued by the
// API (#666); `banepadDeviceSession.ts` holds a flag saying this pad is paired.
//
// ⚠️ «NO CREDENTIAL AT ALL» IS WHAT THIS USED TO SAY, AND IT OVERCLAIMED (#670
// review). A pad paired before the cookie still has its legacy bearer in
// `localStorage` until its FIRST SUCCESSFUL REQUEST seeds the cookie and retires the
// token — and a tablet that was offline when the change shipped reaches that point
// whenever it next comes back, not on a schedule. Steady state and migration window
// are different claims; only the first is credential-free.
//
// The consequence recorded with the decision still stands: the credential is
// AMBIENT, so state-changing `/pad` routes have their own trusted-Origin rule, and
// device auth is its own credential family rather than an inherited one.
//
// Storage-first: every mutation awaits Storage.set BEFORE the caller updates any
// React state (HANDOVER §13).
// ─────────────────────────────────────────────────────────────────────
import { fetchBanepadDevices, persistBanepadDevices } from './api/banepadDevices';
import { fetchPadIdentity, padApiModeActive, pairPadDevice, type PadIdentity } from './api/pad';
import {
  createRegistrationRecord,
  findRegistrationForCourt,
  isBanepadCodeExpired,
  resolveDeviceToken,
  revokeRegistration,
  type BanepadDeviceRegistration,
  type PairResult,
} from '../engine/banepadDevice';

/** Reads the full registration list (tolerant of missing/corrupt demo storage). */
async function readAll(): Promise<BanepadDeviceRegistration[]> {
  try {
    const list = await fetchBanepadDevices();
    if (Array.isArray(list)) return list as BanepadDeviceRegistration[];
  } catch {
    /* ignore — demo storage; start from empty */
  }
  return [];
}

/** Persists the full list. Storage-first: callers update state only after this resolves. */
async function writeAll(list: BanepadDeviceRegistration[]): Promise<void> {
  await persistBanepadDevices(list);
}

/** Lists every registration (pending / paired / revoked). */
export async function listBanepadRegistrations(): Promise<BanepadDeviceRegistration[]> {
  return readAll();
}

/** Returns the active (pending or paired) registration for a court, or null. */
export async function getBanepadRegistrationForCourt(
  courtId: number | string,
): Promise<BanepadDeviceRegistration | null> {
  return findRegistrationForCourt(await readAll(), courtId);
}

/**
 * Creates (or reuses) the registration for a court. Reuses an existing PAIRED
 * registration, or a pending one whose code is still valid (so reopening the
 * modal shows a stable code). If the only pending code has EXPIRED, it mints a
 * fresh one and drops the stale pending(s) for that court — calling this again
 * is how the operator "generates a new code".
 */
export async function createBanepadRegistration(
  courtId: number | string,
  courtName: string,
  opts: { force?: boolean } = {},
): Promise<BanepadDeviceRegistration> {
  const list = await readAll();
  const existing = findRegistrationForCourt(list, courtId);
  // A PAIRED pad is never clobbered, forced or not — replacing its
  // registration would silently unpair a working tablet on the wall.
  if (existing?.status === 'paired') return existing;
  // WITHOUT `force` this is "make sure a code exists": opening the screen
  // twice must not churn the code, which the idempotency test pins.
  //
  // WITH it, the operator pressed "Generer ny kode" and means it. That button
  // used to call the same idempotent path, so for any code that had not
  // expired it did NOTHING — and a migrated club's codes never expire
  // (codeExpiresAt: null), so its pads could not be given a usable code at
  // all. Found on a physical tablet (#216).
  if (!opts.force && existing && !isBanepadCodeExpired(existing)) {
    return existing;
  }
  const reg = createRegistrationRecord(courtId, courtName);
  // Drop any stale/expired pending code for this court so it can't linger.
  const cid = Number(courtId);
  const kept = list.filter((r) => !(Number(r.courtId) === cid && r.status === 'pending'));
  await writeAll([...kept, reg]);
  return reg;
}

/**
 * Pairs the device that entered `registrationCode`. Returns the engine's
 * discriminated result ({ ok:true, registration } | { ok:false, error }).
 */
export async function pairBanepadDevice(registrationCode: string): Promise<PairResult> {
  // ── THE SERVER PAIRS, AND NOTHING ELSE DOES (#185, #663) ─────────
  //
  // `/pad/pair` mints a 256-bit token server-side and keeps only its hash, so the
  // credential is not self-issued and the server can tell a real pad from a tab
  // with a plausible string in localStorage.
  //
  // There used to be a fallback here, taken whenever `Storage.backend !== 'http'`,
  // that minted a device token in the BROWSER (`Math.random`) and wrote it into
  // the club's registry blob. It was unreachable on every deployed configuration —
  // and a weak-credential minter on a branch selected by a transport flag is one
  // deploy-config mistake away from being the credential the product issues.
  //
  // So it is gone, and a build with no API refuses to pair rather than inventing
  // something that looks like a credential. That is the honest failure: CLAUDE.md
  // already warns that the silent localStorage fallback «hides the real problem».
  if (!padApiModeActive()) return { ok: false, error: 'server_required' };

  const out = await pairPadDevice(registrationCode);
  if (out.status === 'refused') {
    // Every refusal maps to `invalid_code`, which is the screen's existing
    // wording. The server already answers ONE error for wrong, expired,
    // revoked and unknown so the endpoint cannot be used to probe which
    // codes exist — and throttling is not worth distinguishing on a kiosk
    // where the operator's next move is the same either way: get a new code.
    return { ok: false, error: 'invalid_code' };
  }
  const reg = registrationFromPadIdentity(out.identity);
  // No registry write: the device row IS the record now, and the club's
  // registry is Sentralen's view of it rather than the pad's own store.
  return { ok: true, registrations: [reg], registration: reg };
}

/**
 * The shape the pad screens already render, built from what the server said.
 *
 * Deliberately a translation rather than a new type: every Banepad component
 * takes a `BanepadDeviceRegistration`, and changing that to thread a second
 * shape through the tree would be a refactor of the renderers inside an auth
 * change. The fields the legacy blob carried and the server does not — the
 * registration code, its expiry — are not invented; the code is spent and the
 * token does not expire with it.
 */
function registrationFromPadIdentity(identity: PadIdentity): BanepadDeviceRegistration {
  const now = Date.now();
  return {
    id: identity.deviceId,
    courtId: identity.court.id,
    courtName: identity.court.name,
    registrationCode: '',
    // ── NO CREDENTIAL IN THE CLIENT REGISTRATION (#668 review) ──────
    //
    // This used to carry the raw token, and `handlePaired` puts the whole
    // registration into React state for the lifetime of the paired session — so
    // «the pad no longer holds a device credential» was true of localStorage and
    // false of application state. The PR claiming it said «nothing in the browser
    // reads it past the callback»; the callback was what kept it.
    //
    // Nothing on this path needs it: #668 removed the token argument from the
    // state, result, heartbeat and messaging transports, and the cookie
    // authenticates all four. The LEGACY registry path still carries its own field
    // (`resolveDeviceToken` matches on it), which is why the type keeps it nullable
    // rather than losing it.
    deviceToken: null,
    status: 'paired',
    createdAt: now,
    codeExpiresAt: now,
    pairedAt: now,
    lastSeenAt: now,
  };
}

/**
 * Resolves a device token to its (paired) registration — the lookup a real pad
 * would do to learn its court.
 *
 * ON THE DEPLOYED PATH THE SERVER ANSWERS THIS (#185): `/pad/me` resolves the
 * hashed token to the device row and returns that row's court. The old production
 * marker «verify a hashed token server-side» is done.
 *
 * The registry read below remains for the memory/test backends, and it is a READ —
 * unlike pairing, which no longer has a client path at all (#663). «Never authorize
 * from a client-supplied courtId» still holds and is why the pad sends no court.
 */
export async function resolveBanepadDevice(
  /**
   * OPTIONAL SINCE #663: the API path identifies the pad by its httpOnly cookie,
   * which no caller can read. It is still a parameter because the legacy registry
   * read below matches on it, and that path is memory/test only.
   */
  deviceToken = '',
): Promise<BanepadDeviceRegistration | null> {
  // THE SERVER SAYS WHICH COURT, ON THE API PATH (#185).
  //
  // The legacy path downloads the club's ENTIRE device registry and matches the
  // token in the browser — which needs a club user's session to read, and puts
  // every pad's credential on every pad. `/pad/me` answers from the device row
  // the token resolves to, so a pad learns only about itself.
  if (padApiModeActive()) {
    // NO TOKEN PASSED (#663): the cookie identifies the pad, and `deviceToken` is
    // only still a parameter for the legacy registry read below.
    const identity = await fetchPadIdentity();
    return identity ? registrationFromPadIdentity(identity) : null;
  }
  return resolveDeviceToken(await readAll(), deviceToken);
}

/** Unpairs/revokes a registration by id (demo reset). Returns the revoked record or null. */
export async function revokeBanepadDevice(
  deviceId: string,
): Promise<BanepadDeviceRegistration | null> {
  const list = await readAll();
  const { registrations, registration } = revokeRegistration(list, deviceId);
  if (registration) await writeAll(registrations);
  return registration;
}
